/*
  <aoa-tabs>: ARIA tabs over server-rendered panels.
  Without JS the tab list stays hidden (it is rendered with the hidden attribute) and every
  panel is visible with its own visible title. With JS:
  - roles tablist / tab / tabpanel, aria-selected, aria-controls, aria-labelledby, roving tabindex;
  - arrow keys, Home and End move the selection (automatic activation);
  - optional sliding indicator ([data-aoa-tabs-indicator]) and links that follow the selected
    tab ([data-aoa-tabs-link] takes the data-href of the selected tab);
  - emits "aoa:tabs-change" with { index } on the element.
*/
if (!customElements.get('aoa-tabs')) {
  let aoaTabsCount = 0;

  class AoaTabs extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tablist = this.own('[data-aoa-tablist]')[0];
      this.tabs = this.own('[data-aoa-tab]');
      this.panels = this.own('[data-aoa-tab-panel]');
      if (!this.tablist || !this.tabs.length || this.tabs.length !== this.panels.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const base = this.id || 'aoa-tabs-' + (aoaTabsCount += 1);

      this.tablist.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        const panel = this.panels[index];
        tab.id = tab.id || base + '-tab-' + index;
        panel.id = panel.id || base + '-panel-' + index;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        // APG: the panel takes focus only when it holds nothing focusable
        if (!panel.querySelector('a[href], button, input, select, textarea, [tabindex="0"]')) panel.tabIndex = 0;
        // The tab names the panel now: the no-JS title would be read twice
        panel.querySelectorAll('[data-aoa-tab-title]').forEach((title) => {
          if (title.closest('aoa-tabs') === this) title.hidden = true;
        });
      });
      this.tablist.hidden = false;

      this.tablist.addEventListener('click', this.onClick, options);
      this.tablist.addEventListener('keydown', this.onKeydown, options);
      this.resizeObserver = new ResizeObserver(() => this.moveIndicator());
      this.resizeObserver.observe(this.tablist);

      const preset = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(Math.max(0, preset), { silent: true });
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.controller = null;
      this.resizeObserver = null;
    }

    // Only this component's parts, not those of a nested <aoa-tabs>
    own(selector) {
      return Array.from(this.querySelectorAll(selector)).filter((element) => element.closest('aoa-tabs') === this);
    }

    select(index, { focus = false, silent = false } = {}) {
      this.index = index;
      this.tabs.forEach((tab, position) => {
        const selected = position === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.panels[position].hidden = !selected;
      });
      const tab = this.tabs[index];
      if (focus) tab.focus();
      if (tab.dataset.href) {
        this.own('[data-aoa-tabs-link]').forEach((link) => {
          link.href = tab.dataset.href;
        });
      }
      this.moveIndicator();
      if (!silent) this.dispatchEvent(new CustomEvent('aoa:tabs-change', { bubbles: true, detail: { index } }));
    }

    moveIndicator() {
      const indicator = this.tablist && this.tablist.querySelector('[data-aoa-tabs-indicator]');
      const tab = this.tabs && this.tabs[this.index];
      if (!indicator || !tab) return;
      this.tablist.style.setProperty('--aoa-tab-x', tab.offsetLeft + 'px');
      this.tablist.style.setProperty('--aoa-tab-w', tab.offsetWidth + 'px');
    }

    onClick = (event) => {
      const tab = event.target.closest('[data-aoa-tab]');
      const index = this.tabs.indexOf(tab);
      if (index > -1 && index !== this.index) this.select(index);
    };

    onKeydown = (event) => {
      const last = this.tabs.length - 1;
      const keys = {
        ArrowRight: this.index === last ? 0 : this.index + 1,
        ArrowLeft: this.index === 0 ? last : this.index - 1,
        Home: 0,
        End: last,
      };
      if (!(event.key in keys) || !event.target.closest('[data-aoa-tab]')) return;
      event.preventDefault();
      this.select(keys[event.key], { focus: true });
    };

    // Theme editor: show the panel that holds the selected block
    reveal(element) {
      const index = this.panels.findIndex((panel) => panel.contains(element));
      if (index > -1 && index !== this.index) this.select(index);
    }
  }

  customElements.define('aoa-tabs', AoaTabs);

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const tabs = event.target.closest('aoa-tabs');
      if (tabs) tabs.reveal(event.target);
    });
  }
}
